import React from "react";
import { BaseListing } from "../../common/BaseListing";
import { Constants } from "./Constants";
import { StringHelper } from "../../../common/Helpers";
import _ from "lodash";
import { toast } from "react-toastify";
import {
  NavigationItem,
  NavigationSettings,
} from "../../common/NavigationSettings";
import {
  CreditCardProgram,
  PaymentStatus,
  PaymentType,
  TransactionStatus,
} from "../../../constants/TransactionConstants";
import { ApplicationID } from "../../../common/Constants";

const CREATED_ON_DEFAULT_RANGE_DAYS = 30;
const CREATED_ON_MAX_LOOKBACK_DAYS = 365;

export class TransactionListing extends BaseListing {
  constructor() {
    super();
    this.state = {
      search: "",
      selectedSearchBy: Constants.SearchBy.ReferenceNumber,
      selectedSearchFrom: Constants.SearchFrom.Live,
      includeHistorical: false,
      createdOnTimeFrame: { from: "", to: "" },
      appId: this.getQueryParam("appId") || 0,
      status: parseInt(this.getQueryParam("status")) || 0,
      paymentType: this.getQueryParam("paymentType") || 0,
      paymentStatus: this.getQueryParam("paymentStatus") || 0,
      creditCardProgram: this.getQueryParam("creditCardProgram") || 0,
    };
  }

  updateUrlParams = () => {
    const { appId, status, paymentType, paymentStatus, creditCardProgram } =
      this.state;
    const queryParams = new URLSearchParams(window.location.search);
    if (appId !== 0) {
      queryParams.set("appId", appId);
    }
    if (status !== 0) {
      queryParams.set("status", status);
    }
    if (paymentType !== 0) {
      queryParams.set("paymentType", paymentType);
    }
    if (paymentStatus !== 0) {
      queryParams.set("paymentStatus", paymentStatus);
    }
    if (creditCardProgram !== 0) {
      queryParams.set("creditCardProgram", creditCardProgram);
    }
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}?${queryParams.toString()}`
    );
  };

  getQueryParam = (name) => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get(name);
  };

  componentDidMount() {
    this.setState(
      { createdOnTimeFrame: TransactionListing.getDefaultCreatedOnTimeFrame() },
      () => super.componentDidMount()
    );
  }

  onIconClick = (id) => {
    const { activeModule } = this.getNavigationSettings();
    return `${activeModule.identifier}/${id}`;
  };

  getNavigationSettings = () => {
    return new NavigationSettings({
      parentModule: new NavigationItem({
        identifier: "transactions",
        name: "Transaction Management",
      }),
      activeModule: new NavigationItem({
        identifier: "transactions",
        name: "Transactions",
      }),
    });
  };

  getDefaultSort = () => "-CreatedOn";

  getApiPath = () => "/api/v1/transactions";

  injectSearchTerm = (queryParameters) => {
    let searchTerm = super.injectSearchTerm(
      queryParameters,
      this.createSearchByParam()
    );
    const {
      includeHistorical,
      createdOnTimeFrame,
      appId,
      paymentType,
      status,
      paymentStatus,
      creditCardProgram,
    } = this.state;
    searchTerm = {
      ...searchTerm,
      includeHistorical,
      includes: "PaymentCredits",
    };

    const criteria = {
      appId,
      paymentType,
      status,
      paymentStatus,
      creditCardProgram,
    };

    Object.entries(criteria).forEach(([key, value]) => {
      if (value) {
        searchTerm[key] = value;
      }
    });

    const { from, to } = createdOnTimeFrame;
    const isReferenceNumberSearch =
      this.state.selectedSearchBy === Constants.SearchBy.ReferenceNumber &&
      !_.isEmpty(this.state.search);
    if (!isReferenceNumberSearch && this.isDateRangeValid(from, to)) {
      searchTerm.creationTimeFrame = `${from},${to}`;
    }

    return searchTerm;
  };

  static getDefaultCreatedOnTimeFrame = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - CREATED_ON_DEFAULT_RANGE_DAYS);
    return {
      from: TransactionListing.toDatetimeLocalValue(from),
      to: TransactionListing.toDatetimeLocalValue(to),
    };
  };

  static toDatetimeLocalValue = (date) => {
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return "";
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  };

  getMinCreatedOnDate = () => {
    const min = new Date();
    min.setDate(min.getDate() - CREATED_ON_MAX_LOOKBACK_DAYS);
    return TransactionListing.toDatetimeLocalValue(min);
  };

  getMaxCreatedOnDate = () => TransactionListing.toDatetimeLocalValue(new Date());

  isDateRangeValid = (startDate, endDate) =>
    !this.getDateRangeValidationError(startDate, endDate);

  getDateRangeValidationError = (startDate, endDate) => {
    if (!startDate || !endDate) {
      return "Created On start and end date are required.";
    }
    if (startDate > endDate) {
      return "Created On start date must be earlier than or equal to end date.";
    }
    if (startDate < this.getMinCreatedOnDate()) {
      return `Created On start date cannot be more than ${CREATED_ON_MAX_LOOKBACK_DAYS} days ago.`;
    }
    if (endDate > this.getMaxCreatedOnDate()) {
      return "Created On end date cannot be later than today.";
    }
    return null;
  };



  onDateChange = (e) => {
    const { name, value } = e.target;
    const { from, to } = this.state.createdOnTimeFrame;
    const createdOnTimeFrame = {
      from: name === "createdOnStartDate" ? value : from,
      to: name === "createdOnEndDate" ? value : to,
    };

    this.setState({ createdOnTimeFrame }, () => {
      const { from, to } = createdOnTimeFrame;
      if (!from || !to) return;

      const error = this.getDateRangeValidationError(from, to);
      if (error) {
        toast.error(error);
        return;
      }
      this.search();
    });
  };

  onResetFilter = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("appId");
    url.searchParams.delete("status");
    url.searchParams.delete("paymentType");
    url.searchParams.delete("paymentStatus");
    url.searchParams.delete("creditCardProgram");
    this.setState(
      {
        appId: 0,
        status: 0,
        paymentType: 0,
        paymentStatus: 0,
        creditCardProgram: 0,
        createdOnTimeFrame: TransactionListing.getDefaultCreatedOnTimeFrame(),
      },
      () => {
        window.history.replaceState({}, "", url.toString());
        this.updateUrlParams();
        this.search();
      }
    );
  };

  onCriteriaChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    this.setState({ [name]: value }, () => {
      this.updateUrlParams();
      this.search();
    });
  };

  generateTableContent = () => {
    const { data } = this.state;
    return (
      <>
        <thead>
          <tr>
            <th></th>
            <th>Reference Number</th>
            <th>Payment Status</th>
            <th>Status</th>
            <th>Sales Channel</th>
            <th>Contact Number</th>
            <th>Email Address</th>
            <th>Payment Type</th>
            <th>Amount</th>
            <th className="pointer" onClick={this.onColumnClick}>
              Updated On
              <i
                className="sort-button fas fa-sort-alpha-down"
                id="UpdatedOn"
                onClick={this.toggleSort}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {data &&
            data.map((item) => (
              <tr
                key={item.id}
                style={{
                  backgroundColor: TransactionStatus.getBackgroundColor(
                    item.status
                  ),
                }}
              >
                <td>
                  <a href={this.onIconClick(item.id)} target={"_blank"}>
                    {" "}
                    <i className="fas fa-external-link-alt"></i>
                  </a>
                </td>
                <td onClick={() => this.onRowClick(item.id)}>
                  {item.referenceNumber}
                </td>
                <td onClick={() => this.onRowClick(item.id)}>
                  <span
                    className={`${PaymentStatus.getColor(item.paymentStatus)}`}
                  >
                    {PaymentStatus.getName(item.paymentStatus)}
                  </span>
                </td>
                <td onClick={() => this.onRowClick(item.id)}>
                  <span
                    className={`${TransactionStatus.getColor(item.status)}`}
                  >
                    {TransactionStatus.getName(item.status)}
                  </span>
                </td>
                <td onClick={() => this.onRowClick(item.id)}>
                  {item.salesChannel}
                </td>
                <td onClick={() => this.onRowClick(item.id)}>
                  {item.contactNumber}
                </td>
                <td onClick={() => this.onRowClick(item.id)}>
                  {item.emailAddress}
                </td>
                <td onClick={() => this.onRowClick(item.id)}>
                  {PaymentType.getName(item.paymentType)}{" "}
                  {item.paymentCredits?.length
                    ? `[${CreditCardProgram.getName(
                        item.paymentCredits[0].creditCardProgram
                      )}]`
                    : ""}
                </td>
                <td onClick={() => this.onRowClick(item.id)}>
                  ${item.grossAmount.toFixed(6)}
                </td>
                <td onClick={() => this.onRowClick(item.id)}>
                  {StringHelper.asDateStringToDisplayDateTimeFormat(
                    item.updatedOn
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </>
    );
  };

  generateExtendedComponents = () => {
    return (
      <>
        <div className="row mx-0">
          <div className="mr-4">{this.generateSearchFunctions()}</div>
          <div>{this.generateCriteriaFilter()}</div>
        </div>
        {this.generateActions()}
      </>
    );
  };

  generateActions = () => {
    return (
      <div className="row form-group input-group mx-0 px-0 col-md-6">
        <input
          className="form-control search"
          id="SearchTxt"
          type="Text"
          placeholder={this.getPlaceHolder()}
          onChange={(e) => this.setState({ search: e.target.value })}
          onKeyDown={this.onKeyDownSearchInput}
        />
        <button className="btn btn-primary search" onClick={this.search}>
          <i className="fas fa-search" /> Search{" "}
        </button>
      </div>
    );
  };

  generateCriteriaFilter() {
    const { appId, status, paymentType, paymentStatus, creditCardProgram, createdOnTimeFrame } =
      this.state;
    const { from: createdOnStartDate, to: createdOnEndDate } = createdOnTimeFrame;
    return (
      <div className="row px-0 mx-xl-0">
        <div className="col-md-6">
          <div className="mb-3">
            <label htmlFor="appId">Sales Channel</label>
            <select
              name="appId"
              id="appId"
              className="form-control"
              value={appId}
              onChange={this.onCriteriaChange}
            >
              <option value="0">All Sales Channel</option>
              {ApplicationID.All.map((x) => (
                <option value={x.value} key={x.value}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label htmlFor="status">Status</label>
            <select
              name="status"
              id="status"
              className="form-control"
              value={status}
              onChange={this.onCriteriaChange}
            >
              <option value="">All Status</option>
              {TransactionStatus.All.map((x) => (
                <option value={x.value} key={x.value}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label htmlFor="paymentType">Payment Type</label>
            <select
              name="paymentType"
              id="paymentType"
              className="form-control"
              value={paymentType}
              onChange={this.onCriteriaChange}
            >
              <option value="0">All Payment Type</option>
              {PaymentType.All.map((x) => (
                <option value={x.value} key={x.value}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label htmlFor="paymentType">Payment Status</label>
            <select
              name="paymentStatus"
              id="paymentStatus"
              className="form-control"
              value={paymentStatus}
              onChange={this.onCriteriaChange}
            >
              <option value="">All Payment Status</option>
              {PaymentStatus.All.map((x) => (
                <option value={x.value} key={x.value}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label htmlFor="creditCardProgram">Credit Card Program</label>
            <select
              name="creditCardProgram"
              id="creditCardProgram"
              className="form-control"
              value={creditCardProgram}
              onChange={this.onCriteriaChange}
            >
              <option value="">All Card Program</option>
              {CreditCardProgram.All.map((x) => (
                <option value={x.value} key={x.value}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="col-md-6">
          <div className="mb-3">
            <label htmlFor="createdOnStartDate">Created On (Start Time)</label>
            <input
              className="form-control"
              type="datetime-local"
              id="createdOnStartDate"
              name="createdOnStartDate"
              value={createdOnStartDate}
              min={this.getMinCreatedOnDate()}
              max={createdOnEndDate || this.getMaxCreatedOnDate()}
              onChange={this.onDateChange}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="createdOnEndDate">Created On (End Time)</label>
            <input
              className="form-control"
              type="datetime-local"
              id="createdOnEndDate"
              name="createdOnEndDate"
              value={createdOnEndDate}
              min={createdOnStartDate || this.getMinCreatedOnDate()}
              max={this.getMaxCreatedOnDate()}
              onChange={this.onDateChange}
            />
          </div>
        </div>
        <div className="col-12 mb-3">
          <button className="btn btn-primary" onClick={this.onResetFilter}>
            Reset Filter
          </button>
        </div>
      </div>
    );
  }

  getPlaceHolder = () => {
    const { selectedSearchBy } = this.state;
    if (selectedSearchBy === Constants.SearchBy.ReferenceNumber) {
      return "Enter Transaction Number";
    }

    if (selectedSearchBy === Constants.SearchBy.CreditCard) {
      return "Enter Credit Card Reference";
    }

    if (selectedSearchBy === Constants.SearchBy.ContactNumber) {
      return "Enter Customer Contact Number";
    }

    return "Enter Customer Email Address";
  };

  generateSearchFunctions = () => {
    const { includeHistorical } = this.state;
    return (
      <div className="form-group px-0">
        <div>
          <label className="form-control-label">Search By:</label>
          <div className="form-check mb-3">
            <div className="radio">
              <label htmlFor="TransNo" className="form-check-label ">
                <input
                  type="radio"
                  id="TransNo"
                  name="SearchBy"
                  value={Constants.SearchBy.ReferenceNumber}
                  checked={
                    this.state.selectedSearchBy ===
                    Constants.SearchBy.ReferenceNumber
                  }
                  className="mr-1"
                  onChange={() =>
                    this.selectSearchBy(Constants.SearchBy.ReferenceNumber)
                  }
                />
                Transaction Number
              </label>
            </div>
            <div className="radio">
              <label htmlFor="CCRef" className="form-check-label ">
                <input
                  type="radio"
                  id="CCRef"
                  name="SearchBy"
                  value={Constants.SearchBy.CreditCard}
                  checked={
                    this.state.selectedSearchBy ===
                    Constants.SearchBy.CreditCard
                  }
                  className="mr-1"
                  placeholder="Enter Credit Card Reference"
                  onChange={() =>
                    this.selectSearchBy(Constants.SearchBy.CreditCard)
                  }
                />
                Credit Card Reference
              </label>
            </div>
            <div className="radio">
              <label htmlFor="ContactNo" className="form-check-label ">
                <input
                  type="radio"
                  id="ContactNo"
                  name="SearchBy"
                  value={Constants.SearchBy.ContactNumber}
                  checked={
                    this.state.selectedSearchBy ===
                    Constants.SearchBy.ContactNumber
                  }
                  className="mr-1"
                  placeholder="Enter Customer Contact Number"
                  onChange={() =>
                    this.selectSearchBy(Constants.SearchBy.ContactNumber)
                  }
                />
                Customer Contact Number
              </label>
            </div>
            <div className="radio">
              <label htmlFor="Email" className="form-check-label ">
                <input
                  type="radio"
                  id="Email"
                  name="SearchBy"
                  value={Constants.SearchBy.EmailAddress}
                  checked={
                    this.state.selectedSearchBy ===
                    Constants.SearchBy.EmailAddress
                  }
                  className="mr-1"
                  placeholder="Enter Customer Email Address"
                  onChange={() =>
                    this.selectSearchBy(Constants.SearchBy.EmailAddress)
                  }
                />
                Customer Email Address
              </label>
            </div>
          </div>
          <label className="form-control-label">Search From:</label>
          <div className="form-check mb-3">
            <div className="radio">
              <label htmlFor="today-and-upcoming" className="form-check-label ">
                <input
                  type="radio"
                  id="today-and-upcoming"
                  className="mr-1"
                  name="searchFrom"
                  value={includeHistorical}
                  onChange={() => this.setState({ includeHistorical: false })}
                  checked={!includeHistorical}
                />
                Today And Upcoming Transaction
              </label>
            </div>
            <div className="radio">
              <label htmlFor="historical" className="form-check-label ">
                <input
                  type="radio"
                  id="historical"
                  className="mr-1"
                  name="searchFrom"
                  value={includeHistorical}
                  onChange={() => this.setState({ includeHistorical: true })}
                  checked={includeHistorical}
                />
                Past Transactions
              </label>
            </div>
          </div>
        </div>
      </div>
    );
  };

  selectSearchBy = (e) => {
    this.setState({
      selectedSearchBy: e,
    });
  };

  createSearchByParam = () => {
    const { search } = this.state;
    if (_.isEmpty(search)) {
      return null;
    }

    if (this.state.selectedSearchBy === Constants.SearchBy.CreditCard) {
      return { creditCardReference: search };
    }

    if (this.state.selectedSearchBy === Constants.SearchBy.ContactNumber) {
      return { contactNumber: search };
    }

    if (this.state.selectedSearchBy === Constants.SearchBy.EmailAddress) {
      return { emailAddress: search };
    }

    return { referenceNumber: search };
  };
}
